import { toZonedTime } from 'date-fns-tz';

import { DateFormat, i18nFormat } from 'utils/date.utils';

import { SiteLocale } from 'constants/i18n.constants';
import { XpSessionFragment } from 'types/api/dato-operations.types';

/** Anything with a slot. */
export type Timed = Pick<XpSessionFragment, 'startTime' | 'endTime'>;

/** Falls back to the reader's own, which still groups the day correctly. */
export const zoneOf = (timezone: string | undefined) =>
  timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

/** A session's start in event time. */
export const startOf = (session: Pick<Timed, 'startTime'>, timezone: string) =>
  session.startTime
    ? toZonedTime(new Date(session.startTime), timezone)
    : undefined;

/** A session's end in event time. */
export const endOf = (session: Pick<Timed, 'endTime'>, timezone: string) =>
  session.endTime
    ? toZonedTime(new Date(session.endTime), timezone)
    : undefined;

/** Pre-formatted slot, e.g. "6:00 PM - 6:45 PM". */
export const slotOf = (
  session: Timed,
  locale: SiteLocale,
  timezone: string,
) => {
  const start = startOf(session, timezone);

  if (!start) return undefined;

  return [start, endOf(session, timezone)]
    .filter((date) => !!date)
    .map((date) => i18nFormat(date, DateFormat.Time, locale))
    .join(' - ');
};

/**
 * A `lineup_xp` has no dates of its own, so its run is whatever its sessions
 * cover. Left as ISO strings for the caller to format in the event's timezone.
 */
export const runOf = (sessions: Array<Timed> | undefined) => {
  const times = (sessions ?? [])
    .flatMap((session) => [session.startTime, session.endTime])
    .filter((time): time is string => !!time)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  return { startDate: times[0], endDate: times[times.length - 1] };
};

interface Day<T> {
  id: string;
  /** Day heading, e.g. "Thursday 4". */
  label: string;
  /** Second line under it, e.g. "Dec 2026". */
  sublabel: string;
  /** Each record with its start already resolved in event time. */
  items: Array<{ value: T; start: Date }>;
}

/**
 * Timed records by the day they start, in running order. Records without a
 * start are dropped: a timetable places them by their time.
 */
export const groupByDay = <T extends Timed>(
  items: Array<T>,
  locale: SiteLocale,
  timezone: string,
): Array<Day<T>> => {
  const days = new Map<string, Day<T>>();

  for (const item of [...items].sort(compareByStart(timezone))) {
    const start = startOf(item, timezone);

    if (!start) continue;

    const id = i18nFormat(start, DateFormat.DayKey, locale);

    const day = days.get(id) ?? {
      id,
      label: i18nFormat(start, DateFormat.WeekdayDay, locale),
      sublabel: i18nFormat(start, DateFormat.ShortMonthYear[locale], locale),
      items: [],
    };

    day.items.push({ value: item, start });
    days.set(id, day);
  }

  return [...days.values()];
};

/** Running order. Sessions with no start sort last. */
export const compareByStart =
  (timezone: string) =>
  (a: Pick<Timed, 'startTime'>, b: Pick<Timed, 'startTime'>) => {
    const [startA, startB] = [startOf(a, timezone), startOf(b, timezone)];

    if (!startA || !startB) return startA ? -1 : Number(!!startB);

    return startA.getTime() - startB.getTime();
  };
